import * as React from "react";
import { Fun, IOWidget, mk_widget, option, Options } from "../widget";

/**
 * The options passed to the [[promise]] widget-builder.
 * @typeparam _i Not used here. Represents the input-type of the underlying IOWidget.
 * @typeparam o is the result-type of the promise.
 */
export interface PromiseOptions<_i,o> extends Options {
  /**
   * A callback which is invoked in case the underlying promise fails.
   *
   * This callback is used to turn erroneous-data of the failed promise into "normal" data of type `o` expected by the widget.
   * In other words, the callback constructs a default promise value from error data.
   * @param reason the exceptional value caught by javascript `catch` clause. It can be any JS value (i.e. both primitive and object value).
   * @return o is the same as the expected returned type of the promise.
   */
  on_fail:(reason:any) => o,

  /**
   * @ignore
   * If not provided, defaults to false
   */
  call_promise_when_receive_new_props?: boolean
}

interface PromiseProps<i,o> {
  options:PromiseOptions<i,o>
  input:i,
  actual_promise:Fun<i,Promise<o>>,
  on_done:(output:o) => void
}

interface PromiseState<_i,_o> { }

/**
 * Don't use directly, use the [[promise]] function instead.
 * @todo maybe change it to `React.FunctionComponent` and hooks, since currently it has not state.
 */
class PromiseComponent<i,o> extends React.Component<PromiseProps<i,o>, PromiseState<i,o>> {
  constructor(props:PromiseProps<i,o>, context:any) {
    super(props, context)

    this.state = {}
  }

  status?:"running" | "done" | "failed"
  run_promise(i:i) {
    if (this.status == "running") return
    this.status = "running"
    this.props.actual_promise(i)
      .then(res => {
              this.status = "done";
              this.props.on_done(res);
            }
           ,reason => {
             this.status="failed";
             if (this.props.options && this.props.options.on_fail)
                 this.props.on_done(this.props.options.on_fail(reason));
           })
  }

  componentWillReceiveProps(newProps:PromiseProps<i,o>) {
    if (option("call_promise_when_receive_new_props", this.props.options)) this.run_promise(newProps.input)
  }

  componentDidMount() {
    this.run_promise(this.props.input)
  }

  render() {
    return <div key={`${option("key", this.props.options)}-inner`} className={this.status} />
  }

}

/**
 * An adapter from a parametric JS-promise into a widget
 *
 * @typeparam i the input parameter of the parametric promise. 
 *
 * When `GET`ting stuff, `i` usually is `Unit` or `void`.
 * @typeparam o the output type of the parametric promise. 
 *
 * When `POST`ing stuff, `o` usually is `Unit` or `void`.
 *
 */
export const promise = function<i, o>(p:Fun<i,Promise<o>>, options:PromiseOptions<i,o>) : IOWidget<i, o> {
  return (input:i) => mk_widget({
    run:(on_done:(output:o) => void) =>
        React.createElement<PromiseProps<i,o>>(PromiseComponent,
                                               { on_done, input,
                                                 /* makes call_promise_when_receive_new_props to default to false */
                                                 options:options.call_promise_when_receive_new_props==undefined?{...options,call_promise_when_receive_new_props:false}:options,
                                                 actual_promise:p,
                                                 key: options.key })})
}

